/**
 * SiteGlue public How It Works — five-step customer journey.
 * Choose → Run Free Scan → Review → Improve With SiteGlue → Verify.
 */

import { SITEGLUE_PRODUCT_NAME } from "~/lib/siteglue/customer-terms";
import {
  AUDIT_INDEX_HREF,
  RUN_MY_FREE_AUDIT_CTA,
  WORDPRESS_PLUGIN_HREF,
} from "~/lib/siteglue/landing-redesign/copy";

export type HowItWorksStepId = "choose" | "scan" | "review" | "improve" | "verify";

export type HowItWorksStepCta = {
  href: string;
  label: string;
  testId: string;
  kind: HowItWorksStepId;
};

export type HowItWorksStep = {
  id: HowItWorksStepId;
  n: string;
  label: string;
  heading: string;
  subheading: string;
  lead: string;
  bullets: string[];
  cta: HowItWorksStepCta;
};

export const HOW_IT_WORKS_STEPS: HowItWorksStep[] = [
  {
    id: "choose",
    n: "01",
    label: "Choose",
    heading: "Choose Your Audit",
    subheading: "ARO, AEO, or CRO",
    lead: "Pick Agent Readiness, Answer Engine Optimization, or Conversion Rate Optimization. Each audit has its own landing page and score.",
    bullets: [
      "ARO — can AI agents discover and use your site",
      "AEO — will answer engines recommend your business",
      "CRO — does the website turn visitors into customers",
    ],
    cta: {
      href: AUDIT_INDEX_HREF,
      label: RUN_MY_FREE_AUDIT_CTA,
      testId: "button-hiw-scan",
      kind: "choose",
    },
  },
  {
    id: "scan",
    n: "02",
    label: "Scan",
    heading: "Run Your Free Scan",
    subheading: "URL only — no signup",
    lead: "Enter your website URL on the audit page and run the free scan. Advanced configuration stays optional.",
    bullets: [
      "No account required to view results",
      "Default benchmarks run automatically",
      "Customize Audit is optional for channels or agent walks",
    ],
    cta: {
      href: AUDIT_INDEX_HREF,
      label: RUN_MY_FREE_AUDIT_CTA,
      testId: "button-hiw-run-scan",
      kind: "scan",
    },
  },
  {
    id: "review",
    n: "03",
    label: "Review",
    heading: "Review Your Results",
    subheading: "Score, benchmarks, recommendations",
    lead: "See one primary score, benchmark evidence, and prioritized recommendations before you change anything.",
    bullets: [
      "Independent ARO, CRO, and AEO reports",
      "Each check links to the Audit Benchmark Library",
      "Historical reports keep the catalog version they were scored with",
    ],
    cta: {
      href: AUDIT_INDEX_HREF,
      label: "See the audits",
      testId: "button-hiw-review",
      kind: "review",
    },
  },
  {
    id: "improve",
    n: "04",
    label: "Improve",
    heading: "Improve With SiteGlue",
    subheading: "WordPress plugin and supported ARO fixes",
    lead: `For WordPress, install the SiteGlue plugin to implement supported ARO improvements. For other platforms, use install artifacts, coding-agent prompts, or Trish-assisted setup. The plugin does not guarantee a higher score or more customers. ${SITEGLUE_PRODUCT_NAME} channels stay configurable after you review results.`,
    bullets: [
      "Supported ARO artifacts: llms.txt, MCP discovery, and structured contact facts",
      "Install via WordPress plugin, WP-CLI where available, or manual files",
      "Trish can help with setup. Do not share WordPress administrator passwords in agent conversations",
      "Non-WordPress sites use install artifacts or assisted setup — we do not advertise unsupported automatic installation",
    ],
    cta: {
      href: WORDPRESS_PLUGIN_HREF,
      label: "SiteGlue WordPress plugin",
      testId: "button-hiw-improve",
      kind: "improve",
    },
  },
  {
    id: "verify",
    n: "05",
    label: "Verify",
    heading: "Verify and Optimize",
    subheading: "Rescan, then monitor connections",
    lead: "Rescan the website, verify improvements, and monitor conversations, connections, and conversions in the dashboard.",
    bullets: [
      "Rescan to confirm supported ARO fixes landed",
      "Inbox and Connection Reporting stay in-app",
      "Moonlight Mode publishes Directory scores only from a ready scan",
    ],
    cta: {
      href: "/directory",
      label: "View the Directory",
      testId: "button-hiw-verify",
      kind: "verify",
    },
  },
];

export const HOW_IT_WORKS_PROGRESS_LABELS = HOW_IT_WORKS_STEPS.map((s) => ({
  id: s.id,
  label: s.label,
}));

/** @deprecated Use HOW_IT_WORKS_STEPS */
export const HOW_ONBOARDING_STAGES = HOW_IT_WORKS_STEPS;
/** @deprecated Use HOW_IT_WORKS_PROGRESS_LABELS */
export const ONBOARDING_PROGRESS_LABELS = HOW_IT_WORKS_PROGRESS_LABELS;
